'use client';

import { Box, Button, Flex, freddyTheme, SxProps } from '@coolblue-development/freddy';
import { CheckboxChecked, CheckboxUnchecked } from '@coolblue-development/icons';
import { useTranslation } from '@i18n/useTranslation';
import { type ReactNode, useId } from 'react';

interface ExpandableBoxProps {
  isExpanded: boolean;
  isSubscribed: boolean;
  onClick: () => void;
  expandedContent: ReactNode;
  children: ReactNode;
}

function CheckboxIcon({ isChecked }: { isChecked: boolean }) {
  return (
    <Box
      mt={50}
      sx={{
        width: freddyTheme.spacing[400],
        height: freddyTheme.spacing[400]
      }}
    >
      {isChecked ? (
        <CheckboxChecked aria-hidden='true' focusable='false' />
      ) : (
        <CheckboxUnchecked aria-hidden='true' focusable='false' />
      )}
    </Box>
  );
}

export default function ExpandableBox({
  isExpanded,
  isSubscribed,
  onClick,
  expandedContent,
  children
}: ExpandableBoxProps) {
  const emailSubscriptionFormId = useId();
  const { t } = useTranslation('webshop.productPage.freeWashing');

  const getButtonStyles = (isExpanded: boolean): SxProps => ({
    width: '100%',
    textAlign: 'left',
    padding: freddyTheme.spacing[400],
    border: isExpanded
      ? 'none'
      : `${freddyTheme.borderWidth[25]} solid ${freddyTheme.color.border.interactiveBase}`,
    borderRadius: isExpanded ? '0' : freddyTheme.spacing[100],
    borderBottomLeftRadius: isExpanded ? '0' : freddyTheme.spacing[100],
    borderBottomRightRadius: isExpanded ? '0' : freddyTheme.spacing[100],
    boxShadow: 'none',
    '&:hover': {
      boxShadow: isExpanded ? 'none' : freddyTheme.shadow.subtle,
      borderColor: 'none'
    }
  });

  if (isSubscribed) {
    return (
      <Flex
        alignItems='flex-start'
        gap={300}
        border={`${freddyTheme.borderWidth[50]} solid ${freddyTheme.color.border.interactiveSelected}`}
        borderRadius={100}
        p={400}
        backgroundColor='interactiveSelected'
      >
        <CheckboxIcon isChecked={isSubscribed} />
        {children}
      </Flex>
    );
  }

  return (
    <Box
      sx={
        isExpanded
          ? {
              border: `${freddyTheme.borderWidth[25]} solid ${freddyTheme.color.border.interactiveBase}`,
              borderRadius: freddyTheme.spacing[100],
              overflow: 'hidden'
            }
          : {}
      }
    >
      <Button
        type='button'
        variant='secondary'
        onClick={onClick}
        aria-expanded={isExpanded}
        aria-controls={emailSubscriptionFormId}
        sx={getButtonStyles(isExpanded)}
      >
        <Flex alignItems='flex-start' gap={300}>
          <CheckboxIcon isChecked={isSubscribed} />
          {children}
        </Flex>
      </Button>

      <Box
        id={emailSubscriptionFormId}
        role='form'
        aria-label={t('formHeading')}
        hidden={!isExpanded}
        borderTop='none'
        p={`0 ${freddyTheme.spacing[400]} ${freddyTheme.spacing[400]} ${freddyTheme.spacing[1100]}`}
        sx={{
          borderBottomLeftRadius: freddyTheme.spacing[100],
          borderBottomRightRadius: freddyTheme.spacing[100]
        }}
      >
        {expandedContent}
      </Box>
    </Box>
  );
}
